import classnames from 'classnames/bind'
import { useRef } from 'react'
import { useTranslate } from 'react-polyglot'
import { GlobalThemeColors } from '~/@types/colors'
import { GlobalTextPreset } from '~/@types/text-preset'
import { GlobalTextStyling } from '~/@types/text-styling'

import Slider, { useSliderState } from '@unlikelystudio/react-slider'

import Ratio from '~/components/Abstracts/Ratio'
import SliderArrows from '~/components/SliderArrows'
import Spinner from '~/components/Spinner'
import Image from '~/components/UI/Image'
import { ProductHeaderSubtitle } from '~/components/UI/ProductHeader/HeaderSubtitle'

import { useProductHeaderProvider } from '~/providers/ProductPageProvider'
import { useStyle } from '~/providers/StyleProvider'
import { useUserSelectionProvider } from '~/providers/UserSelectionProvider'

import { useGetStateInformationsFromUserSelection } from '~/hooks/useGetStateInformationsFromUserSelection'

import { CUSTOMIZER } from '~/data/dictionary'

import css from './styles.module.scss'

const cx = classnames.bind(css)

export interface PackProductSelectorProps {
  className?: string
}

const IMAGE_SIZES = [{ breakpoint: 'lg', ratio: 100 / 1440 }, { ratio: 2 / 12 }]

export function PackProductSelector({ className }: PackProductSelectorProps) {
  const { setUserSelection } = useUserSelectionProvider()
  const { isLoadingTabProducts } = useProductHeaderProvider()
  const { activeTabIndex, activeTab, products } =
    useGetStateInformationsFromUserSelection()
  const t = useTranslate()

  const titleStyle = useStyle({
    textPreset: GlobalTextPreset.Cta10Primary,
    textStyling: GlobalTextStyling.UpperCase,
    color: GlobalThemeColors.Gray1ToBlack,
  })

  const [
    {
      slideIndex,
      maxSlideIndex,
      enableDrag,
      nextSlide,
      prevSlide,
      setSlideIndex,
    },
    setSliderState,
  ] = useSliderState()

  const renderSlider = () =>
    products?.map((product, i) => (
      <button
        key={`product_selector_${activeTabIndex}_${i}`}
        aria-label={t(CUSTOMIZER.BUNDLE_SELECT, {
          product: product?.productName,
        })}
        className={cx(css.item, {
          active: product?.shopifyId === activeTab?.productId,
        })}
        onClick={() => {
          setSlideIndex(i)
          setUserSelection((prev) => {
            const newState = prev.map((tab) =>
              tab.isActive
                ? {
                    ...tab,
                    productId: product?.shopifyId,
                    variantId: product?.defaultVariant?.id,
                  }
                : tab,
            )

            return newState
          })
        }}>
        <Image
          className={cx(css.image)}
          layout="fill"
          objectFit="cover"
          draggable="false"
          ratio={98 / 120}
          sizesFromBreakpoints={IMAGE_SIZES}
          asPlaceholder
          src={product?.featuredImage}
        />
        <span className={cx(css.title, titleStyle)}>{product?.title}</span>
      </button>
    ))

  const customSliderRef = useRef()
  return (
    <div
      className={cx(css.PackProductSelector, className, { active: true })}
      ref={customSliderRef}>
      <ProductHeaderSubtitle
        className={css.subtitle}
        text={t(CUSTOMIZER.SELECT_PRODUCT(products?.[0]?.productType))}
      />
      {isLoadingTabProducts ? (
        <Slider className={cx(css.placeHolderSlider)}>
          {renderSlider()}
          {Array(5)
            .fill(null)
            .map((_, index) => (
              <div
                className={cx(css.placeHolder, css.item)}
                key={`product_selector_placeholder_${activeTabIndex}_${index}`}>
                <div className={cx(css.ratioContainer)}>
                  <Ratio ratio={98 / 120} className={cx(css.ratio)}>
                    {() => null}
                  </Ratio>
                  <Spinner className={cx(css.spinner)} />
                </div>
                <span className={cx(css.title, titleStyle)}>&nbsp;</span>
              </div>
            ))}
        </Slider>
      ) : (
        <Slider
          className={cx(css.productsImages)}
          snap
          autoBlockSlideIndexChange
          setSliderState={setSliderState}>
          {renderSlider()}
        </Slider>
      )}

      {enableDrag && (
        <div className={cx(css.arrowsContainer)}>
          <SliderArrows
            index={slideIndex}
            length={maxSlideIndex + 1}
            buttonsLayout="xs"
            nextSlide={nextSlide}
            prevSlide={prevSlide}
          />
        </div>
      )}
    </div>
  )
}
